import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Box,
    Button,
    Grid,
    Typography,
    Divider,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
} from '@mui/material'
import {
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Download as DownloadIcon,
    Email as EmailIcon
} from '@mui/icons-material'
import moment from 'moment-timezone'
import { InvoiceResponse, clientInvoicesApi } from '@assembly/api/clientInvoices'
import { useAppStore, useSnackbars } from '@assembly/hooks'
import { SnackbarType } from '@assembly/contexts'
import { RoutePaths } from '@assembly/constants'
import EditInvoiceDialog from './EditInvoiceDialog'
import EmailInvoiceModal from './EmailInvoiceModal'

export default function ClientInvoiceDetail() {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { setAppBarTitle } = useAppStore()
    const { addAlert } = useSnackbars()

    const [invoice, setInvoice] = useState<InvoiceResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [emailDialogOpen, setEmailDialogOpen] = useState(false)

    useEffect(() => {
        if (id) {
            fetchInvoice(id)
        } else {
            setError('Invoice ID is required')
            setLoading(false)
        }
    }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (invoice) {
            setAppBarTitle(`Invoice - ${invoice.invoiceNumber || id}`)
        } else {
            setAppBarTitle('Invoice Details')
        }
    }, [invoice, id, setAppBarTitle])

    const fetchInvoice = async (invoiceId: string) => {
        try {
            setLoading(true)
            setError(null)
            const response = await clientInvoicesApi.getInvoiceById(invoiceId)
            setInvoice(response.data)
        } catch (error: any) {
            console.error('Error fetching invoice:', error)
            setError(error.response?.data?.message || 'Failed to fetch invoice')
            addAlert({
                message: 'Failed to fetch invoice details',
                type: SnackbarType.Error,
            })
        } finally {
            setLoading(false)
        }
    }

    const handleEdit = () => {
        setEditDialogOpen(true)
    }

    const handleEditSuccess = () => {
        setEditDialogOpen(false)
        // Refetch the invoice to get the latest data
        if (id) {
            fetchInvoice(id)
        }
        addAlert({
            message: 'Invoice updated successfully',
            type: SnackbarType.Success,
        })
    }

    const handleExportPDF = async () => {
        if (!invoice) return

        try {
            const { getPDFGeneratorType } = await import('@assembly/utils/pdf/pdfConfig')
            const generatorType = getPDFGeneratorType()

            if (generatorType === 'optimized') {
                // Use optimized PDF generator for smaller file sizes (~50-200KB)
                const { downloadOptimizedInvoicePDF } = await import('@assembly/utils/pdf/optimizedInvoiceGenerator')
                downloadOptimizedInvoicePDF(invoice)
            } else {
                // Use HTML-based PDF generator for original styling (~20-30MB)
                const { downloadInvoiceHTMLPDF } = await import('@assembly/utils/pdf/htmlInvoiceGenerator')
                await downloadInvoiceHTMLPDF(invoice)
            }

            addAlert({
                message: 'PDF exported successfully',
                type: SnackbarType.Success,
            })
        } catch (error) {
            console.error('Error exporting PDF:', error)
            addAlert({
                message: 'Failed to export PDF',
                type: SnackbarType.Error,
            })
        }
    }

    const handleEmailInvoice = () => {
        setEmailDialogOpen(true)
    }

    const handleBack = () => {
        navigate(RoutePaths.ClientInvoices)
    }

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        }).format(amount)
    }

    const formatDate = (timestamp: number) => {
        return moment(timestamp).format('MMM DD, YYYY')
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'PAID':
                return 'success'
            case 'PARTIALLY_PAID':
                return 'info'
            case 'PENDING':
                return 'warning'
            case 'SENT':
                return 'info'
            case 'OVERDUE':
                return 'error'
            case 'DRAFT':
                return 'default'
            case 'CANCELLED':
                return 'error'
            case 'REFUNDED':
                return 'warning'
            case 'ERROR':
                return 'error'
            default:
                return 'default'
        }
    }

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        )
    }

    if (error || !invoice) {
        return (
            <Box p={3}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mb: 2 }}
                >
                    Back to Invoices
                </Button>
                <Alert severity="error">
                    {error || 'Invoice not found'}
                </Alert>
            </Box>
        )
    }

    return (
        <Box p={3}>
            {/* Header with navigation and actions */}
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                >
                    Back to Invoices
                </Button>
                <Box display="flex" gap={1}>
                    <Button
                        startIcon={<EditIcon />}
                        variant="outlined"
                        onClick={handleEdit}
                    >
                        Edit
                    </Button>
                    <Button
                        startIcon={<EmailIcon />}
                        variant="outlined"
                        onClick={handleEmailInvoice}
                        color="primary"
                    >
                        Send Email
                    </Button>
                    <Button
                        startIcon={<DownloadIcon />}
                        variant="contained"
                        onClick={handleExportPDF}
                    >
                        Export PDF
                    </Button>
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Header Information */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    Bill To
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {invoice.organization?.name || 'N/A'}
                                </Typography>
                                {invoice.organization?.email && (
                                    <Typography variant="body2" color="text.secondary">
                                        {invoice.organization.email}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Box display="flex" justifyContent="flex-end">
                                    <Box textAlign="right">
                                        <Typography variant="h6" gutterBottom>
                                            Invoice Details
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Invoice #:</strong> {invoice.invoiceNumber || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Status:</strong>{' '}
                                            <Chip
                                                label={invoice.status || 'N/A'}
                                                color={getStatusColor(invoice.status || 'DRAFT') as any}
                                                size="small"
                                            />
                                        </Typography>
                                        {invoice.statusMessage && (
                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Status Message:</strong> {invoice.statusMessage}
                                            </Typography>
                                        )}
                                        <Typography variant="body2">
                                            <strong>Type:</strong> {invoice.type || 'N/A'}
                                        </Typography>
                                        <Typography variant="body2">
                                            <strong>Payment Terms:</strong> {invoice.paymentTerms || 'N/A'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Dates and Project Information */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    Project Information
                                </Typography>
                                <Typography variant="body1" fontWeight="medium">
                                    {invoice.assemblyLine?.name || 'N/A'}
                                </Typography>
                                {invoice.assemblyLine?.projectType && (
                                    <Typography variant="body2" color="text.secondary">
                                        Type: {invoice.assemblyLine.projectType}
                                    </Typography>
                                )}
                                {invoice.assemblyLine?.status && (
                                    <Typography variant="body2" color="text.secondary">
                                        Status: {invoice.assemblyLine.status}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Typography variant="h6" gutterBottom>
                                    Important Dates
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Issue Date:</strong> {invoice.issueDate ? formatDate(invoice.issueDate) : 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Due Date:</strong> {invoice.dueDate ? formatDate(invoice.dueDate) : 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Billing Period:</strong> {invoice.billingPeriodStart || 'N/A'} - {invoice.billingPeriodEnd || 'N/A'}
                                </Typography>
                                {invoice.paidDate && (
                                    <Typography variant="body2">
                                        <strong>Paid Date:</strong> {formatDate(invoice.paidDate)}
                                    </Typography>
                                )}
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* Description and Notes */}
                {(invoice.description || invoice.notes) && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Grid container spacing={3}>
                                {invoice.description && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>
                                            Description
                                        </Typography>
                                        <Typography variant="body1">
                                            {invoice.description || 'No description provided'}
                                        </Typography>
                                    </Grid>
                                )}

                                {invoice.notes && (
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="h6" gutterBottom>
                                            Internal Notes
                                        </Typography>
                                        <Typography variant="body1">
                                            {invoice.notes || 'No notes provided'}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Paper>
                    </Grid>
                )}

                {/* Line Items */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Line Items
                        </Typography>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Description</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell align="right">Quantity</TableCell>
                                        <TableCell>Unit</TableCell>
                                        <TableCell align="right">Unit Price</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoice.lineItems && invoice.lineItems.length > 0 ? invoice.lineItems.map((item, index) => (
                                        item && (
                                            <TableRow key={index}>
                                                <TableCell>{item.description || 'N/A'}</TableCell>
                                                <TableCell>
                                                    <Chip label={item.type || 'N/A'} size="small" variant="outlined" />
                                                </TableCell>
                                                <TableCell align="right">{item.quantity || 0}</TableCell>
                                                <TableCell>{item.unit || 'N/A'}</TableCell>
                                                <TableCell align="right">{item.unitPrice ? formatCurrency(item.unitPrice) : 'N/A'}</TableCell>
                                                <TableCell align="right">{item.totalAmount ? formatCurrency(item.totalAmount) : 'N/A'}</TableCell>
                                            </TableRow>
                                        )
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center">
                                                <Typography variant="body2" color="text.secondary">
                                                    No line items found
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Totals */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, mb: 3 }}>
                        <Box display="flex" justifyContent="flex-end">
                            <Box width="300px">
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography>Subtotal:</Typography>
                                    <Typography>{invoice.subtotalAmount ? formatCurrency(invoice.subtotalAmount) : 'N/A'}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography>Tax:</Typography>
                                    <Typography>{invoice.taxAmount ? formatCurrency(invoice.taxAmount) : 'N/A'}</Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="h6">Total Amount:</Typography>
                                    <Typography variant="h6">{invoice.totalAmount ? formatCurrency(invoice.totalAmount) : 'N/A'}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography>Paid Amount:</Typography>
                                    <Typography>{invoice.paidAmount ? formatCurrency(invoice.paidAmount) : 'N/A'}</Typography>
                                </Box>
                                <Divider sx={{ my: 1 }} />
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography variant="h6" color={invoice.remainingAmount && invoice.remainingAmount > 0 ? 'error' : 'success'}>
                                        {invoice.remainingAmount && invoice.remainingAmount > 0 ? 'Amount Due:' : 'Fully Paid'}
                                    </Typography>
                                    <Typography variant="h6" color={invoice.remainingAmount && invoice.remainingAmount > 0 ? 'error' : 'success'}>
                                        {invoice.remainingAmount && invoice.remainingAmount > 0 ? formatCurrency(invoice.remainingAmount) : 'Paid'}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Paper>
                </Grid>

                {/* Payment History */}
                {invoice.payments && invoice.payments.length > 0 && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Payment History
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>Method</TableCell>
                                            <TableCell>Reference</TableCell>
                                            <TableCell align="right">Amount</TableCell>
                                            <TableCell>Status</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.payments.map((payment, index) => (
                                            payment && (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {payment.paymentDate ? formatDate(payment.paymentDate) : 'N/A'}
                                                    </TableCell>
                                                    <TableCell>{payment.method || 'N/A'}</TableCell>
                                                    <TableCell>{payment.reference || 'N/A'}</TableCell>
                                                    <TableCell align="right">{payment.amount ? formatCurrency(payment.amount) : 'N/A'}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            label={payment.status || 'N/A'}
                                                            color={payment.status === 'COMPLETED' ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                )}

                {/* Status History */}
                {invoice.statusHistory && invoice.statusHistory.length > 0 && (
                    <Grid item xs={12}>
                        <Paper sx={{ p: 3, mb: 3 }}>
                            <Typography variant="h6" gutterBottom>
                                Status History
                            </Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Date</TableCell>
                                            <TableCell>From</TableCell>
                                            <TableCell>To</TableCell>
                                            <TableCell>Changed By</TableCell>
                                            <TableCell>Reason</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {invoice.statusHistory.map((change, index) => (
                                            change && (
                                                <TableRow key={index}>
                                                    <TableCell>
                                                        {change.timestamp ? formatDate(change.timestamp) : 'N/A'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={change.fromStatus || 'N/A'} size="small" variant="outlined" />
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip label={change.toStatus || 'N/A'} size="small" />
                                                    </TableCell>
                                                    <TableCell>{change.changedBy || 'N/A'}</TableCell>
                                                    <TableCell>{change.reason || 'N/A'}</TableCell>
                                                </TableRow>
                                            )
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>
                )}

                {/* Additional Information */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Additional Information
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="body2">
                                    <strong>Created:</strong> {invoice.createdDate ? formatDate(invoice.createdDate) : 'N/A'}
                                </Typography>
                                <Typography variant="body2">
                                    <strong>Last Updated:</strong> {invoice.lastUpdated ? formatDate(invoice.lastUpdated) : 'N/A'}
                                </Typography>
                                {invoice.createdBy && (
                                    <Typography variant="body2">
                                        <strong>Created By:</strong> {invoice.createdBy}
                                    </Typography>
                                )}
                                {invoice.lastModifiedBy && (
                                    <Typography variant="body2">
                                        <strong>Last Modified By:</strong> {invoice.lastModifiedBy}
                                    </Typography>
                                )}
                            </Grid>

                            <Grid item xs={12} md={6}>
                                {invoice.stripeInvoiceId && (
                                    <Typography variant="body2">
                                        <strong>Stripe Invoice ID:</strong> {invoice.stripeInvoiceId}
                                    </Typography>
                                )}
                                {invoice.stripeCustomerId && (
                                    <Typography variant="body2">
                                        <strong>Stripe Customer ID:</strong> {invoice.stripeCustomerId}
                                    </Typography>
                                )}
                                {invoice.externalReference && (
                                    <Typography variant="body2">
                                        <strong>External Reference:</strong> {invoice.externalReference}
                                    </Typography>
                                )}
                                <Typography variant="body2">
                                    <strong>Currency:</strong> {invoice.currency || 'USD'}
                                </Typography>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>

            {/* Edit Dialog */}
            {editDialogOpen && invoice && (
                <EditInvoiceDialog
                    open={editDialogOpen}
                    invoice={invoice}
                    onClose={() => setEditDialogOpen(false)}
                    onSuccess={handleEditSuccess}
                />
            )}

            {/* Email Dialog */}
            <EmailInvoiceModal
                open={emailDialogOpen}
                invoice={invoice}
                onClose={() => setEmailDialogOpen(false)}
            />
        </Box>
    )
}
